import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= and detect read-only mode.
// FP3-043 Phase A follow-up #9 — Goal Builder is purely a mutation
// surface; admin family users (even without shadow) must be blocked.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { useStudentWorkspaceReadOnly } from "@/components/layout/ReadOnlyProviderBanner";
import { ArrowLeft, Sparkle, Target, Plus, Check } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import api from "@/lib/api";
import useCurrentProviderStudents from "@/hooks/useCurrentProviderStudents";

const CADENCES = ["weekly", "bi-weekly", "monthly", "quarterly"];

// FP3-019 — Goal instructional level chips. Provider-selected; MUST be
// kept separate from the student's enrolled grade. Many students get
// services because their current skill level is below their enrolled
// grade — never use enrolled grade as a substitute.
const GOAL_LEVEL_OPTIONS = [
  "Pre-K / Kindergarten",
  "1st grade",
  "2nd grade",
  "3rd grade",
  "4th grade",
  "5th grade",
  "Middle school",
  "High school",
  "Functional / life skills",
  "Custom",
];

// FP3-019 — Role-aware chip catalogs. Reference shape: each role maps
// to {skill_areas, target_skills_by_area}. Resource Teacher and SLP
// must not bleed into each other.
const SLP_SKILL_AREAS = [
  "Articulation",
  "Phonological Processes",
  "Auditory Processing",
  "Receptive Language",
  "Expressive Language",
  "Pragmatics / Social Communication",
  "Vocabulary",
  "Grammar / Syntax",
  "Narrative Language",
  "Fluency",
  "Voice",
  "AAC",
  "Intelligibility",
  "Carryover / Generalization",
];

const RESOURCE_SKILL_AREAS = [
  "Reading Comprehension",
  "Decoding",
  "Reading Fluency",
  "Phonological Awareness",
  "Written Expression",
  "Math Problem Solving",
  "Executive Functioning",
  "Work Completion",
  "Vocabulary",
  "Study Skills",
  "Classroom Participation / Access",
];

const SLP_TARGET_SKILLS = {
  "Articulation": [
    "/r/", "vocalic /r/", "/s/", "/z/", "/l/", "/l/ blends", "/k/", "/g/",
    "/t/", "/d/", "/f/", "/v/", "/th/", "/sh/", "/ch/", "/j/",
    "final consonants", "blends",
  ],
  // FP3-020 — Phonological Processes gets its own chip catalog so
  // providers don't have to type one in. Multi-select supported.
  "Phonological Processes": [
    "final consonant deletion",
    "cluster reduction",
    "fronting",
    "stopping",
    "gliding",
    "weak syllable deletion",
    "syllable reduction",
    "initial consonant deletion",
    "backing",
    "deaffrication",
    "assimilation",
    "prevocalic voicing",
    "postvocalic devoicing",
    "stridency deletion",
  ],
  // FP3-021 — Auditory Processing target chips, ordered by clinical
  // hierarchy (foundation → memory → connection → structure → logic).
  // Multi-select supported. This is for goal drafting only — does not
  // imply a diagnosis. The AI is told to avoid overdiagnosis language.
  "Auditory Processing": [
    // Foundation / discrimination
    "auditory discrimination",
    "phonemic discrimination",
    "minimal pair discrimination",
    "auditory figure-ground",
    "listening with background noise",
    // Storage / memory
    "auditory memory",
    "sequential auditory memory",
    "auditory span",
    "recalling details from spoken information",
    "following multi-step directions",
    // Connection / association & closure
    "auditory association",
    "auditory closure",
    "categorization from spoken words",
    "verbal associations",
    // Structure / synthesis
    "sound blending",
    "phoneme blending",
    "syllable blending",
    "auditory synthesis",
    "syntactic processing from spoken input",
    "understanding word order",
    // Logic / cohesion
    "auditory inferencing",
    "predicting outcomes from spoken information",
    "verbal reasoning",
    "understanding ambiguity",
    "understanding idioms",
    "figurative language",
    "sarcasm",
    "abstract auditory reasoning",
    "cause/effect from spoken scenarios",
  ],
  "Receptive Language": [
    "following directions", "answering WH questions", "comparing and contrasting",
    "vocabulary meaning", "listening comprehension",
  ],
  "Expressive Language": [
    "grammar markers", "sentence expansion", "vocabulary meaning",
    "comparing and contrasting", "narrative retell", "inferencing",
  ],
  "Pragmatics / Social Communication": [
    "topic maintenance", "perspective-taking", "communication repair",
    "turn-taking", "initiating conversation", "indirect language",
  ],
  "Fluency": ["easy onset", "stretchy speech", "self-monitoring", "self-correction"],
  "Vocabulary": ["semantic features", "categorization", "vocabulary meaning"],
  "Grammar / Syntax": ["sentence formulation", "verb tense", "pronoun use"],
  "Narrative Language": ["story grammar", "narrative retell", "sequencing"],
};

const RESOURCE_TARGET_SKILLS = {
  "Reading Comprehension": [
    "main idea", "supporting details", "sequencing", "inferencing",
    "vocabulary in context", "summarizing",
  ],
  "Decoding": ["decoding CVC words", "decoding multisyllabic words", "phonemic awareness"],
  "Reading Fluency": [
    "words correct per minute", "passage accuracy", "reading rate",
    "expression / prosody",
  ],
  "Phonological Awareness": [
    "phonemic awareness", "blending", "segmenting", "rhyming",
  ],
  "Written Expression": [
    "capitalization", "punctuation", "complete sentence",
    "sentence expansion", "topic sentence", "supporting details",
    "paragraph organization", "editing/revising",
  ],
  "Math Problem Solving": [
    "single-step word problems", "multi-step word problems",
    "operation selection", "explaining reasoning", "math vocabulary",
    "computation accuracy",
  ],
  "Executive Functioning": [
    "task initiation", "planning steps", "self-monitoring",
    "transition between tasks",
  ],
  "Work Completion": ["assignment completion", "on-task duration"],
  "Study Skills": ["note-taking", "study planning"],
};

const SUPPORT_LEVEL_OPTIONS = [
  "independent",
  "with visual support",
  "with graphic organizer",
  "with verbal cue",
  "with no more than one cue",
  "with modeling",
  "in structured task",
  "in classroom routine",
  "during conversation",
  "during grade-level activity",
  "Custom",
];

const MEASUREMENT_STYLE_OPTIONS = [
  "percent accuracy",
  "correct / total trials",
  "rubric rating",
  "weekly probe",
  "curriculum-based measure",
  "work sample review",
  "conversational sample",
  "teacher observation",
  "progress monitoring task",
  "words correct per minute",
  "Custom",
];

const CRITERIA_PATTERN_OPTIONS = [
  "80% accuracy across 3 sessions",
  "4 out of 5 opportunities across 3 sessions",
  "3 consecutive data collection opportunities",
  "weekly probe average",
  "reduce cues over time",
  "Custom",
];

// FP3-019 — Map provider role to chip catalogs. Resource Teacher and
// Special Education Teacher both map to the academic catalog.
function chipsForRole(role) {
  const academic = (role || "").toLowerCase();
  if (academic.includes("resource") || academic.includes("special education teacher")) {
    return {
      skill_areas: RESOURCE_SKILL_AREAS,
      target_skills_by_area: RESOURCE_TARGET_SKILLS,
    };
  }
  if (academic === "slp" || academic.includes("speech")) {
    return {
      skill_areas: SLP_SKILL_AREAS,
      target_skills_by_area: SLP_TARGET_SKILLS,
    };
  }
  // Generic fallback — surface SLP areas (current default persona).
  return {
    skill_areas: SLP_SKILL_AREAS,
    target_skills_by_area: SLP_TARGET_SKILLS,
  };
}

// FP3-020 — Skill areas that allow multi-select target chips.
// Articulation goals routinely cover multiple sounds (/k/+/g/, /s/+/z/);
// phonological processes goals also commonly stack two processes
// (final-consonant-deletion + cluster-reduction).
// FP3-021 — Auditory Processing also commonly stacks subskills
// (auditory memory + multi-step directions, etc.).
const MULTI_SELECT_AREAS = new Set([
  "Articulation",
  "Phonological Processes",
  "Auditory Processing",
]);

// FP3-078E — Speech production ladder. Surfaced ONLY when the
// chip-guided skill area is Articulation or Phonological Processes.
// The selected level is passed to the AI and woven naturally into the
// drafted goal wording, target skill breakdown, measurable behavior,
// and practice example. Request-only; never persisted on the goal.
const SPEECH_PRODUCTION_AREAS = new Set([
  "Articulation",
  "Phonological Processes",
]);
const SPEECH_PRODUCTION_LEVELS = [
  "sound level",
  "word level",
  "phrase level",
  "sentence level",
  "reading level",
  "structured conversational level",
  "spontaneous conversation level",
];

// FP3-020 — Multi-select variant of ChipGroup. `value` is an array of
// strings; clicking a chip toggles membership. Visually identical to
// the single-select chip when the array length is 0.
function MultiChipGroup({ testid, options, value, onChange }) {
  const set = new Set(value || []);
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const selected = set.has(opt);
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testid}-opt-${opt
              .replace(/[^a-zA-Z0-9]+/g, "-")
              .replace(/^-|-$/g, "")
              .toLowerCase()}`}
            onClick={() => {
              const next = new Set(set);
              if (selected) next.delete(opt);
              else next.add(opt);
              onChange([...next]);
            }}
            className={
              "px-2.5 py-1 rounded-full text-[12px] border transition-colors " +
              (selected
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white text-[#374151] border-[#E5E3DB] hover:bg-[#F9F8F6]")
            }
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// FP3-019 — Compact chip rendering helper. Selected chip is filled;
// unselected chips are outlined. Custom value handled inline by caller.
function ChipGroup({ testid, options, value, onSelect }) {
  return (
    <div data-testid={testid} className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testid}-opt-${opt
              .replace(/[^a-zA-Z0-9]+/g, "-")
              .replace(/^-|-$/g, "")
              .toLowerCase()}`}
            onClick={() => onSelect(selected ? "" : opt)}
            className={
              "px-2.5 py-1 rounded-full text-[12px] border transition-colors " +
              (selected
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white text-[#374151] border-[#E5E3DB] hover:bg-[#F9F8F6]")
            }
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// FP3-017 — Calm, role-neutral reminders surfaced when the AI flags
// fields the provider should fill in. Keyed by `missing_info` strings
// the backend returns (see /api/goals/ai-generate response shape).
const MISSING_INFO_REMINDERS = {
  baseline:
    "Add a baseline to make this goal easier to measure.",
  measurement_method:
    "Choose how progress will be measured (probe, work sample, structured task, observation tally).",
  criteria:
    "Add the accuracy / criteria target (e.g. 80% across 3 sessions).",
  target_skill:
    "Sharpen the target skill — what exactly should the student do?",
  timeframe:
    "Add a timeframe (across N consecutive opportunities, sessions, or weeks).",
};

// FP3-017 — Lightweight client-side fallback for the goal-area label
// shown in the Suggested Draft preview heading. The proper taxonomy
// already lives on the server; this is just a friendly title-case so
// we don't show kebab-case keys to the provider in the preview.
function label_for_key_fallback(key) {
  if (!key) return "";
  return key
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Iter 45k — Legacy category keys still saved on older goals. We
// pre-populate the Goal Area dropdown with these aliases so historical
// goals open without an empty selector. Once the provider edits and
// saves, the form writes the canonical key from the role-aware list.
const LEGACY_CATEGORY_LABELS = {
  articulation: "Articulation (legacy)",
  language: "Language (legacy)",
  fluency: "Fluency (legacy)",
  pragmatics: "Pragmatics (legacy)",
  voice: "Voice (legacy)",
  OT: "OT (legacy)",
  "social-emotional": "Social-Emotional (legacy)",
};

export default function GoalBuilder() {
  const navigate = useShadowNavigate();
  const readOnly = useStudentWorkspaceReadOnly();
  const [searchParams] = useSearchParams();
  const { goalId } = useParams();
  const preselectedStudentId = searchParams.get("studentId");

  // Iter 45g — student picker scoped to current provider's caseload via
  // shared hook. You can't accidentally author a goal against someone
  // else's student.
  const { provider, students } = useCurrentProviderStudents();
  // Iter 45k — Goal Area taxonomy is role-aware. SLP sees SLP areas
  // only, OT sees OT, etc. Loaded once the provider role is known.
  const [goalAreas, setGoalAreas] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  // FP3-017 — Optional free-form context (teacher concerns, eval excerpt,
  // recent performance). Stays in the AI request only; never persisted.
  const [aiContext, setAiContext] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  // FP3-017 — Structured AI draft preview. Surfaced between AI Assist
  // and the form so the provider can review the draft before it
  // overwrites their in-progress edits. `null` until the model returns.
  const [aiDraft, setAiDraft] = useState(null);
  // FP3-019 — Chip-guided drafting. All optional, request-only, never
  // persisted. Goal level is provider-controlled and intentionally
  // separate from the student's enrolled grade.
  const [chipGoalLevel, setChipGoalLevel] = useState("");
  const [chipGoalLevelCustom, setChipGoalLevelCustom] = useState("");
  const [chipSkillArea, setChipSkillArea] = useState("");
  // FP3-020 — `chipTargetSkill` (single) becomes `chipTargets` (multi).
  // For multi-select areas (Articulation, Phonological Processes) the
  // array can carry multiple sounds/processes. Single-select areas
  // simply use a 1-element array. Custom value is appended via
  // `chipTargetSkillCustom`.
  const [chipTargets, setChipTargets] = useState([]);
  const [chipTargetSkillCustom, setChipTargetSkillCustom] = useState("");
  // FP3-078E — Speech production ladder. Only meaningful when
  // chipSkillArea is Articulation or Phonological Processes.
  const [chipSpeechLevel, setChipSpeechLevel] = useState("");
  const [chipSupport, setChipSupport] = useState("");
  const [chipSupportCustom, setChipSupportCustom] = useState("");
  const [chipMeasurement, setChipMeasurement] = useState("");
  const [chipMeasurementCustom, setChipMeasurementCustom] = useState("");
  const [chipCriteria, setChipCriteria] = useState("");
  const [chipCriteriaCustom, setChipCriteriaCustom] = useState("");
  // FP3-019 — Compact draft panel by default; provider can expand the
  // structured breakdown to see target_skill / measurable_behavior etc.
  const [draftBreakdownOpen, setDraftBreakdownOpen] = useState(false);
  // FP3-020 — Collapsible AI Assist + Context. Both default closed so
  // the page lands quietly on the manual Goal Details form.
  const [aiAssistOpen, setAiAssistOpen] = useState(false);
  const [aiContextOpen, setAiContextOpen] = useState(false);
  // FP3-020 — In-progress flag for the manual "Generate example from
  // goal" button (separate endpoint; doesn't disable Draft Goal).
  const [practiceLoading, setPracticeLoading] = useState(false);
  // FP3-020 — Manual practice-example field is collapsed by default.
  // Auto-expands when the form already carries a value (edit mode).
  const [practiceFieldOpen, setPracticeFieldOpen] = useState(false);
  // FP3-034C — Goal-Plan-aware "Create goal from target" flow.
  // When the URL carries `planTargetId`, we treat this as a Goal
  // Plan-originated draft: show a contextual banner up top + a
  // dedicated "Generate goal" button inside Goal Details. The
  // existing prefill plumbing below still runs, so the form is
  // pre-populated with the target's skill / area / etc. before AI.
  const [goalPlanContext, setGoalPlanContext] = useState(null);
  const [goalPlanContextLoading, setGoalPlanContextLoading] = useState(false);
  const [generatingFromTarget, setGeneratingFromTarget] = useState(false);
  // FP3-034C — Per-call generator error (separate from the
  // chip-guided AI Assist error). Replaces a plain toast so the
  // banner shows the specific reason (budget / rate-limit / etc.)
  // and a Try-again button.
  const [generatorError, setGeneratorError] = useState(null);

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    student_id: preselectedStudentId || "",
    category: "",
    specific_skill: "",
    goal_text: "",
    baseline: "",
    current_mastery: 0,
    target_mastery: 80,
    target_date: "",
    criteria: "",
    cadence: "weekly",
    prompts_cueing: "",
    status: "draft",
    iep_goal_reference: "",
    linked_assessment_id: "",
    // Iter 45l — Objectives removed from Add Goal flow. Generated
    // objectives weren't tracked in live sessions or reports, and
    // school providers primarily work from annual goals. Field is
    // preserved on payloads only to avoid wiping existing objective
    // data on legacy goal documents during edits. May return later
    // as an optional "Goal Milestones" feature with real session
    // tracking; do NOT surface this in the UI today.
    objectives: [],
    // FP3-020 — Optional, provider-facing meeting-friendly explanation.
    // Saved with the goal; displayed on the Goal Progress page.
    practice_example: "",
  });

  // Iter 45k — fetch role-scoped Goal Areas as soon as we know the
  // current provider's role. Falls back to GENERIC if the role isn't
  // recognised by the backend taxonomy.
  // FP3-028B — Skip the fetch entirely until the role is known. The
  // previous behavior issued a `role=""` request (generic domains:
  // Communication, Academic, Motor, etc.) and a moment later issued
  // the correctly-scoped request — but in React StrictMode the two
  // responses could arrive out of order, leaving the dropdown stuck
  // on the generic options for Riley/Resource Teacher. Waiting for
  // role removes the race and means we only ever paint the correct
  // role-scoped taxonomy.
  useEffect(() => {
    const role = provider?.role || "";
    if (!role) {
      // Provider not loaded yet — leave goalAreas empty. The Select
      // placeholder reads "Loading…" until the role-scoped fetch
      // resolves.
      return;
    }
    let active = true;
    api
      .getGoalAreas({ role })
      .then((r) => {
        if (!active) return;
        setGoalAreas(r.data?.areas || []);
      })
      .catch(() => {
        if (!active) return;
        setGoalAreas([]);
      });
    return () => {
      active = false;
    };
  }, [provider?.role]);

  useEffect(() => {
    // Iter 45g — students/provider come from useCurrentProviderStudents.
    if (goalId) {
      api.getGoals({ student_id: "" }).then((r) => {
        const goal = r.data.find((g) => g.id === goalId);
        if (goal) {
          setForm({
            student_id: goal.student_id,
            category: goal.category,
            specific_skill: goal.specific_skill || "",
            goal_text: goal.goal_text,
            baseline: goal.baseline,
            current_mastery: goal.current_mastery,
            target_mastery: goal.target_mastery,
            target_date: goal.target_date,
            criteria: goal.criteria,
            cadence: goal.cadence,
            prompts_cueing: goal.prompts_cueing,
            status: goal.status,
            iep_goal_reference: goal.iep_goal_reference,
            linked_assessment_id: goal.linked_assessment_id,
            objectives: goal.objectives || [],
            practice_example: goal.practice_example || "",
          });
          // FP3-078E — When loading an existing goal that already
          // carries a practice example, default the section to open
          // so the provider can see it without an extra click. The
          // provider can still collapse it via the toggle.
          if (goal.practice_example && goal.practice_example.trim()) {
            setPracticeFieldOpen(true);
          }
        }
      });
    }
  }, [goalId]);

  // FP3-034 — "Create Goal from Target" prefill from Goal Plan tab.
  // When the URL carries `prefillGoalArea` / `prefillTargetSkill` /
  // `prefillInstructionalLevel` / `prefillRationale`, drop those
  // values onto the form + chip state so the provider lands on a
  // ready-to-review draft. We DO NOT auto-save and we DO NOT call
  // AI Assist — the provider still clicks through the normal flow.
  // Runs once on mount when the prefill params are present.
  //
  // FP3-034C — Additionally, when `planTargetId` is present we
  // fetch the canonical target context from the backend so the
  // banner + "Generate goal" button know the long-term area, the
  // suggested focus label, and the linked document ids — all
  // server-validated, never trusted from the URL.
  useEffect(() => {
    if (goalId) return; // edit mode handled above
    const prefillGoalArea = searchParams.get("prefillGoalArea") || "";
    const prefillTargetSkill = searchParams.get("prefillTargetSkill") || "";
    const prefillLevel = searchParams.get("prefillInstructionalLevel") || "";
    const prefillRationale = searchParams.get("prefillRationale") || "";
    const planTargetId = searchParams.get("planTargetId") || "";
    const studentIdParam = searchParams.get("studentId") || preselectedStudentId || "";

    if (
      !prefillGoalArea &&
      !prefillTargetSkill &&
      !prefillLevel &&
      !prefillRationale &&
      !planTargetId
    ) {
      return;
    }
    setForm((f) => ({
      ...f,
      category: prefillGoalArea || f.category,
      specific_skill: prefillTargetSkill || f.specific_skill,
    }));
    if (prefillTargetSkill) {
      setChipTargets((cur) =>
        cur && cur.length > 0 ? cur : [prefillTargetSkill]
      );
    }
    if (prefillLevel) {
      setChipGoalLevel((cur) => cur || prefillLevel);
    }
    if (prefillRationale) {
      // Surface the rationale as optional AI context — handy if the
      // provider opts to use AI Assist next. Never persisted.
      setAiContext((cur) => cur || prefillRationale);
      setAiContextOpen(true);
    }

    // FP3-034C — Fetch the authoritative Goal Plan context. The
    // backend re-runs visibility checks, so we never trust the URL
    // for anything sensitive (only the ids).
    if (planTargetId && studentIdParam) {
      setGoalPlanContextLoading(true);
      api
        .getGoalPlanTargetPrefill(studentIdParam, planTargetId)
        .then((r) => {
          setGoalPlanContext({
            student_id: studentIdParam,
            plan_target_id: planTargetId,
            label: r.data?.label || prefillTargetSkill || "",
            need_area_id: r.data?.need_area_id || "",
            need_area_label: r.data?.need_area_label || "",
            need_area_domain: r.data?.need_area_domain || "",
            goal_area: r.data?.goal_area || "",
            target_skill: r.data?.target_skill || "",
            instructional_level: r.data?.instructional_level || "",
            description: r.data?.description || "",
            rationale: r.data?.rationale || prefillRationale || "",
            status: r.data?.status || "",
            linked_goal_id: r.data?.linked_goal_id || "",
            related_document_ids: r.data?.related_document_ids || [],
          });
        })
        .catch(() => {
          // Target inaccessible / deleted — fall back to URL-only
          // prefill. The banner won't render but the form still
          // works.
          setGoalPlanContext(null);
        })
        .finally(() => setGoalPlanContextLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStudent = students.find((s) => s.id === form.student_id);
  // FP3-019 — Resolve role-aware chip catalog once the provider role is known.
  const chipCatalog = chipsForRole(provider?.role || "");

  const handleAIGenerate = async () => {
    // FP3-020 — Allow chip-only drafting when the provider has selected
    // enough chips even if the description box is empty. Minimum chip
    // signal: goal level + skill area + at least one target (chip or
    // custom). Otherwise require a description.
    const goalLevelResolved =
      chipGoalLevel === "Custom" ? chipGoalLevelCustom.trim() : chipGoalLevel;
    const targetCustomTrimmed = chipTargetSkillCustom.trim();
    const hasEnoughChips =
      Boolean(goalLevelResolved) &&
      Boolean(chipSkillArea) &&
      ((chipTargets || []).length > 0 || Boolean(targetCustomTrimmed));
    if (!aiPrompt.trim() && !hasEnoughChips) {
      toast.error(
        "Choose a goal level, skill area, and target skill, or type a short description."
      );
      return;
    }
    setAiLoading(true);
    try {
      // FP3-019 — Resolve chip values, swapping in custom strings when
      // the provider chose the "Custom" sentinel. All chip values are
      // request-only — never persisted on the saved goal document.
      const goalLevel = goalLevelResolved;
      const supportLevel =
        chipSupport === "Custom" ? chipSupportCustom.trim() : chipSupport;
      const measurementStyle =
        chipMeasurement === "Custom"
          ? chipMeasurementCustom.trim()
          : chipMeasurement;
      const criteriaPattern =
        chipCriteria === "Custom" ? chipCriteriaCustom.trim() : chipCriteria;
      // FP3-020 — Compose the multi-target list. Chip selections plus
      // an optional custom value (which can combine WITH selected chips,
      // not replace them — providers asked for /k/+/g/+custom).
      const targetSkillsList = [...(chipTargets || [])];
      if (targetCustomTrimmed) targetSkillsList.push(targetCustomTrimmed);
      // Backwards-compat single-skill string for any legacy consumer.
      const legacySpecificSkill =
        targetSkillsList.length > 0
          ? targetSkillsList.join(", ")
          : form.specific_skill || "";
      const res = await api.generateGoalAI({
        description: aiPrompt,
        student_name: selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name}` : "",
        category: form.category || "",
        provider_role: provider?.role || "",
        specific_skill: legacySpecificSkill,
        target_skills: targetSkillsList,
        baseline: form.baseline || "",
        prompts_cueing: form.prompts_cueing || "",
        target_mastery: form.target_mastery || 80,
        cadence: form.cadence || "weekly",
        extra_context: aiContext || "",
        goal_level: goalLevel,
        student_grade: selectedStudent?.grade ? `Grade ${selectedStudent.grade}` : "",
        support_level: supportLevel,
        measurement_style: measurementStyle,
        criteria_pattern: criteriaPattern,
        // FP3-078E — Speech production ladder. Only sent when the
        // current skill area is Articulation / Phonological Processes
        // AND a level is picked. Backend tolerates "" (treated as
        // absent).
        speech_production_level:
          SPEECH_PRODUCTION_AREAS.has(chipSkillArea) ? chipSpeechLevel : "",
      });
      // FP3-017 — Stage the structured draft for the provider's review
      // instead of silently overwriting the form. The provider must
      // explicitly click "Apply to form" before any field is filled.
      // FP3-019 — Compact panel by default; breakdown collapsed.
      setAiDraft(res.data || null);
      setDraftBreakdownOpen(false);
      toast.success("Draft ready — review below");
    } catch (e) {
      console.error(e);
      toast.error("AI generation failed. Try again.");
    } finally {
      setAiLoading(false);
    }
  };

  // FP3-020 — Manual goals can request just a practice example based
  // on the current goal text. Read-only endpoint; never persists.
  // Only fills the form's `practice_example` when it's currently
  // empty (mirrors `applyDraftToForm`'s "don't overwrite edits" rule).
  const handleGeneratePracticeExample = async () => {
    const goalText = (form.goal_text || "").trim();
    if (!goalText) {
      toast.error("Add the goal text first, then generate the example.");
      return;
    }
    setPracticeLoading(true);
    try {
      const targetSkillsList = [...(chipTargets || [])];
      const targetCustom = chipTargetSkillCustom.trim();
      if (targetCustom) targetSkillsList.push(targetCustom);
      const res = await api.generatePracticeExample({
        goal_text: goalText,
        provider_role: provider?.role || "",
        goal_level:
          chipGoalLevel === "Custom" ? chipGoalLevelCustom.trim() : chipGoalLevel,
        student_grade: selectedStudent?.grade ? `Grade ${selectedStudent.grade}` : "",
        category: form.category || "",
        target_skills: targetSkillsList,
        support_level:
          chipSupport === "Custom" ? chipSupportCustom.trim() : chipSupport,
        measurement_style:
          chipMeasurement === "Custom"
            ? chipMeasurementCustom.trim()
            : chipMeasurement,
        extra_context: aiContext || "",
        // FP3-078E — Speech production ladder for the practice
        // example. Same Articulation / Phonological Processes gate.
        speech_production_level:
          SPEECH_PRODUCTION_AREAS.has(chipSkillArea) ? chipSpeechLevel : "",
      });
      const ex = res.data?.practice_example || "";
      if (!ex) {
        toast.error("AI did not return an example. Try again.");
        return;
      }
      setForm((f) => {
        if (f.practice_example && f.practice_example.trim()) {
          // Preserve provider edits — manual override required to replace.
          toast.message("Existing example kept. Clear it first to replace.");
          return f;
        }
        return { ...f, practice_example: ex };
      });
      toast.success("Example generated");
    } catch (e) {
      console.error(e);
      toast.error("Generate example failed. Try again.");
    } finally {
      setPracticeLoading(false);
    }
  };

  // FP3-017 — Explicit, provider-controlled merge from AI draft → form.
  // Only writes fields the provider has not already started editing,
  // protecting in-progress work. Provider can still edit any field
  // afterward in the structured form below.
  const applyDraftToForm = () => {
    if (!aiDraft) return;
    setForm((f) => {
      const merged = { ...f };
      // Always-safe overwrite when target field is empty; otherwise
      // require the AI's value to be non-empty.
      const maybeSet = (key, value) => {
        if (!value) return;
        if (!f[key] || f[key].trim?.() === "") {
          merged[key] = value;
        }
      };
      maybeSet("goal_text", aiDraft.goal_text);
      maybeSet("criteria", aiDraft.criteria);
      // Anti-hallucination: only fill baseline if the model actually
      // produced one (backend strips it when provider supplied none).
      maybeSet("baseline", aiDraft.baseline_suggestion);
      // Iter 45l — never apply objectives to the form.
      if (aiDraft.category && !f.category) merged.category = aiDraft.category;
      // Specific skill nudge — fill only when provider hasn't.
      maybeSet("specific_skill", aiDraft.target_skill);
      // FP3-020 — Practice example, only into the form's empty slot.
      maybeSet("practice_example", aiDraft.practice_example);
      return merged;
    });
    toast.success("Applied to form");
  };

  const dismissDraft = () => setAiDraft(null);

  const handleSave = async (status) => {
    if (!form.student_id || !form.goal_text) {
      toast.error("Please select a student and provide goal text");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        status,
        provider_id: provider?.id || "",
      };
      let savedGoal = null;
      if (goalId) {
        const r = await api.updateGoal(goalId, payload);
        savedGoal = r?.data || null;
        toast.success("Goal updated");
      } else {
        const r = await api.createGoal(payload);
        savedGoal = r?.data || null;
        toast.success("Goal created");
      }
      // FP3-034C — Back-link: if we arrived from a Goal Plan target,
      // PATCH the target with the newly-created goal id so the Goal
      // Plan tab now shows "Open goal" instead of "Create goal" for
      // that row. Failures here are non-fatal: the goal is already
      // saved and the provider can re-link manually later.
      const newGoalId = savedGoal?.id || savedGoal?._id || goalId;
      if (
        !goalId &&
        goalPlanContext?.plan_target_id &&
        goalPlanContext?.student_id &&
        newGoalId
      ) {
        try {
          await api.updateGoalPlanTarget(
            goalPlanContext.student_id,
            goalPlanContext.plan_target_id,
            {
              linked_goal_id: newGoalId,
              // Match the saved status — keep the target visible in
              // Current Focus once the live goal is active, otherwise
              // leave its status alone.
              ...(status === "active" ? { status: "current_focus" } : {}),
            }
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[FP3-034C] back-link patch failed", err);
        }
      }
      navigate(`/students/${form.student_id}`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save goal");
    } finally {
      setSaving(false);
    }
  };

  // FP3-034C — "Generate goal" button (visible only inside the Goal
  // Plan flow). Calls the backend draft endpoint that reloads
  // authorized context server-side, then drops the response onto
  // the form. Provider still reviews and clicks Save.
  const handleGenerateFromTarget = async () => {
    if (!goalPlanContext?.plan_target_id || !goalPlanContext?.student_id) {
      return;
    }
    setGeneratingFromTarget(true);
    setGeneratorError(null);
    try {
      const r = await api.draftGoalFromTarget(
        goalPlanContext.student_id,
        goalPlanContext.plan_target_id,
        {}
      );
      const d = r.data || {};
      setForm((f) => ({
        ...f,
        goal_text: d.goal_text || f.goal_text,
        baseline: d.baseline || f.baseline,
        criteria: d.criteria || f.criteria,
        target_mastery:
          typeof d.target_mastery_pct === "number"
            ? d.target_mastery_pct
            : f.target_mastery,
        prompts_cueing: d.prompts_cueing || f.prompts_cueing,
        practice_example: d.practice_example || f.practice_example,
        iep_goal_reference: d.iep_goal_reference || f.iep_goal_reference,
      }));
      // Practice-example field is collapsed by default; auto-expand
      // when the generator actually filled it.
      if (d.practice_example) setPracticeFieldOpen(true);
      toast.success("Goal drafted — review and save.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status || 0;
      if (detail && typeof detail === "object" && detail.message) {
        setGeneratorError({
          code: detail.error_code || "ai_upstream_error",
          message: detail.message,
        });
      } else if (typeof detail === "string" && detail.trim()) {
        setGeneratorError({ code: "ai_upstream_error", message: detail });
      } else if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        setGeneratorError({
          code: "ai_timeout",
          message:
            "The AI took too long to draft the goal. Try Generate goal again.",
        });
      } else if (status === 502 || status === 503 || status === 504) {
        setGeneratorError({
          code: "ai_unavailable",
          message:
            "The AI service didn't respond in time. Wait a minute and Try again. If it keeps failing, top up the Universal Key (Profile → Universal Key → Add Balance).",
        });
      } else {
        setGeneratorError({
          code: "ai_unknown",
          message: `The goal drafter returned an unexpected response (HTTP ${status}). Try again, or edit the fields manually.`,
        });
      }
    } finally {
      setGeneratingFromTarget(false);
    }
  };

  const updateForm = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // FP3-035F Issue 2 — Goal Builder is purely a create/edit surface.
  // In read-only shadow mode the entire page is blocked behind a
  // calm explanation; the admin should use Exit to Admin to leave.
  if (readOnly.active) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="goal-builder-readonly">
        <button
          data-testid="goal-builder-readonly-back"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
          <CardContent className="p-8 text-center space-y-3">
            <Target size={36} weight="duotone" className="mx-auto text-[#9C6B2E]" />
            <h2 className="text-lg font-semibold text-[#1F2937] heading-font">
              Goal Builder is unavailable in read-only mode
            </h2>
            <p className="text-sm text-[#6B7280]">
              {readOnly.mode === "admin_oversight"
                ? "You are viewing this app as an administrator. Goal authoring is a provider workflow, so it is disabled for admin oversight."
                : "You're viewing this provider's workspace as an admin. Creating or editing goals is disabled while shadowing. Use Exit to Admin to return to your normal controls."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto animate-fade-in" data-testid="goal-builder">
      <button
        data-testid="back-button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] transition-colors mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <h1 className="text-2xl font-semibold text-[#1F2937] heading-font tracking-tight mb-6" data-testid="goal-builder-title">
        {goalId ? "Edit Goal" : "Build a Goal"}
      </h1>

      {/* FP3-034C — Goal Plan context banner. Renders ONLY when the
          provider arrived from a Goal Plan suggested target (the
          backend prefill endpoint validated access). Makes it
          unambiguous which suggested focus this goal is being
          drafted for, surfaces the long-term area + rationale, and
          links back to the Goal Plan tab. */}
      {!goalId && goalPlanContext && (
        <Card
          data-testid="goal-plan-context-banner"
          className="bg-[#E8F0EB] rounded-2xl border border-[#A8C6B5] shadow-sm mb-6"
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Target
                size={20}
                weight="duotone"
                className="text-[#1E3F33] mt-0.5 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[#1E3F33]">
                  Creating goal from Goal Plan suggested focus
                </div>
                <div
                  data-testid="goal-plan-context-focus"
                  className="text-[14px] font-semibold text-[#1F2937] heading-font mt-1"
                >
                  {goalPlanContext.label || goalPlanContext.target_skill || "Suggested target"}
                </div>
                {goalPlanContext.need_area_label && (
                  <div
                    data-testid="goal-plan-context-need-area"
                    className="text-[11.5px] text-[#1E3F33] mt-1.5"
                  >
                    Long-term area:{" "}
                    <span className="font-semibold">
                      {goalPlanContext.need_area_label}
                    </span>
                  </div>
                )}
                {goalPlanContext.rationale && (
                  <p
                    data-testid="goal-plan-context-rationale"
                    className="text-[11.5px] text-[#3F4756] mt-2 italic line-clamp-3"
                  >
                    {goalPlanContext.rationale}
                  </p>
                )}
                {(goalPlanContext.status === "needs_current_data" ||
                  goalPlanContext.status === "suggested") && (
                  <span
                    data-testid="goal-plan-context-status"
                    className="inline-block mt-2 text-[10px] uppercase tracking-[0.06em] font-semibold rounded-full px-1.5 py-0.5 border bg-white text-[#1E3F33] border-[#A8C6B5]"
                  >
                    {goalPlanContext.status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <Button
                type="button"
                data-testid="goal-plan-context-cancel"
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate(
                    `/students/${goalPlanContext.student_id}?tab=goal-plan`
                  )
                }
                className="h-8 text-[11.5px] border-[#A8C6B5] text-[#1E3F33] hover:bg-white"
              >
                Back to Goal Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Assist Section */}
      <Card data-testid="ai-assist-card" className="bg-[#EDF2F7] rounded-2xl border border-[#d4dfe8] shadow-sm mb-6">
        <CardContent className="p-5">
          {/* FP3-020 — Compact-by-default header. The full chip guide
              + description box only render when the provider opens
              the helper, so the manual Goal Details form stays the
              dominant page content. */}
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Sparkle size={20} weight="duotone" className="text-[#4A6B8C]" />
              <h2 className="text-sm font-semibold text-[#4A6B8C] heading-font">
                AI Goal Assistant
              </h2>
              {provider?.role && (
                <span
                  data-testid="ai-role-badge"
                  className="ml-1 text-[11px] text-[#4A6B8C]/80 bg-white/60 px-2 py-0.5 rounded-full border border-[#d4dfe8]"
                >
                  {provider.role}
                </span>
              )}
            </div>
            <Button
              data-testid="ai-assist-toggle"
              variant="outline"
              size="sm"
              onClick={() => setAiAssistOpen((v) => !v)}
              className="border-[#d4dfe8] text-[#4A6B8C] hover:bg-white rounded-lg text-xs"
            >
              {aiAssistOpen ? "Hide AI options" : "Guide with AI"}
            </Button>
          </div>
          {!aiAssistOpen && (
            <p
              data-testid="ai-assist-collapsed"
              className="text-[11px] text-[#4A6B8C]/80 mt-2"
            >
              Use chips or notes to draft a goal. Manual goal entry still works without AI.
            </p>
          )}

          {aiAssistOpen && (
            <div data-testid="ai-assist-expanded" className="mt-4">
          <p className="text-xs text-[#4A6B8C]/80 mb-3">
            Describe the student's needs and the AI will draft a measurable annual goal with a structured breakdown. Review the suggestion before applying it to the form — nothing is saved automatically.
          </p>

          {/* FP3-019 — Chip-guided drafting. Optional. The provider can
              still type freely or skip chips entirely; manual save does
              not require any chip selection. Goal instructional level
              is intentionally separate from the student's enrolled grade
              (many students get services because they're below grade). */}
          <div
            data-testid="chip-guide-section"
            className="mb-4 p-3 rounded-lg bg-white/50 border border-[#d4dfe8]"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#4A6B8C]">
                Guide the AI draft
                <span className="ml-2 normal-case font-normal text-[#4A6B8C]/70">· optional</span>
              </h3>
              {selectedStudent?.grade && (
                <div
                  data-testid="student-grade-context"
                  className="text-[11px] text-[#6B7280] bg-white px-2 py-0.5 rounded-full border border-[#E5E3DB]"
                >
                  Student grade · Grade {selectedStudent.grade}
                  {chipGoalLevel && (
                    <span className="ml-2">
                      · Goal level ·{" "}
                      <span className="text-[#1E3F33] font-medium">
                        {chipGoalLevel === "Custom"
                          ? chipGoalLevelCustom || "Custom"
                          : chipGoalLevel}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <p className="text-[11px] text-[#4A6B8C]/70 mb-3">
              Choose a few details to help draft the goal. You can still type your own goal or edit everything before saving.
            </p>

            {/* Goal instructional level — distinct from enrolled grade */}
            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                Goal instructional level
                <span className="ml-2 normal-case font-normal text-[#4A6B8C]/60">
                  drives draft language; not the same as enrolled grade
                </span>
              </Label>
              <div data-testid="goal-level-chip-group">
                <ChipGroup
                  testid="ai-chip-goal-level"
                  options={GOAL_LEVEL_OPTIONS}
                  value={chipGoalLevel}
                  onSelect={setChipGoalLevel}
                />
                {chipGoalLevel === "Custom" && (
                  <Input
                    data-testid="ai-chip-goal-level-custom"
                    value={chipGoalLevelCustom}
                    onChange={(e) => setChipGoalLevelCustom(e.target.value)}
                    placeholder="e.g., early elementary / sentence level"
                    className="mt-2 bg-white border-[#d4dfe8] text-sm"
                  />
                )}
              </div>
            </div>

            {/* Skill area — role-aware */}
            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                Skill area
              </Label>
              <ChipGroup
                testid="ai-chip-skill-area"
                options={chipCatalog.skill_areas}
                value={chipSkillArea}
                onSelect={(v) => {
                  setChipSkillArea(v);
                  // FP3-020 — Reset target chip selections when skill area
                  // changes; the catalog of valid options is now different.
                  setChipTargets([]);
                  setChipTargetSkillCustom("");
                  // FP3-078E — Reset speech production level too. The
                  // ladder is only meaningful for Articulation /
                  // Phonological Processes.
                  setChipSpeechLevel("");
                }}
              />
            </div>

            {/* Target skill — narrows by skill area. FP3-020:
                Articulation and Phonological Processes are multi-select
                (providers stack /k/+/g/, or final-consonant-deletion +
                cluster-reduction). Custom target can combine WITH the
                selected chips, not replace them. */}
            {chipSkillArea && (chipCatalog.target_skills_by_area[chipSkillArea] || []).length > 0 && (
              <div className="mb-3">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                  Target skill
                  {MULTI_SELECT_AREAS.has(chipSkillArea) && (
                    <span className="ml-2 normal-case font-normal text-[#4A6B8C]/70">
                      · multi-select supported
                    </span>
                  )}
                </Label>
                {MULTI_SELECT_AREAS.has(chipSkillArea) ? (
                  <MultiChipGroup
                    testid="ai-chip-target-skill"
                    options={chipCatalog.target_skills_by_area[chipSkillArea]}
                    value={chipTargets}
                    onChange={setChipTargets}
                  />
                ) : (
                  <ChipGroup
                    testid="ai-chip-target-skill"
                    options={[
                      ...(chipCatalog.target_skills_by_area[chipSkillArea] || []),
                      "Custom",
                    ]}
                    value={chipTargets[0] || ""}
                    onSelect={(v) => setChipTargets(v ? [v] : [])}
                  />
                )}
                {/* Custom input — for multi-select areas it always
                    renders alongside the chips; for single-select it
                    renders only when the Custom sentinel is picked. */}
                {(MULTI_SELECT_AREAS.has(chipSkillArea) || chipTargets[0] === "Custom") && (
                  <Input
                    data-testid="ai-chip-target-skill-custom"
                    value={chipTargetSkillCustom}
                    onChange={(e) => setChipTargetSkillCustom(e.target.value)}
                    placeholder={
                      MULTI_SELECT_AREAS.has(chipSkillArea)
                        ? "Optional custom target — combines with selected chips above"
                        : "e.g., main idea and 2 supporting details"
                    }
                    className="mt-2 bg-white border-[#d4dfe8] text-sm"
                  />
                )}
                {/* Calm reminder if the provider stacks too many sounds /
                    processes — does not block; goals can still be drafted. */}
                {MULTI_SELECT_AREAS.has(chipSkillArea) && chipTargets.length >= 4 && (
                  <p
                    data-testid="multi-target-reminder"
                    className="mt-2 text-[11px] text-[#8C5F00]"
                  >
                    Heads-up: multiple targets selected ({chipTargets.length}) — the goal may become broad. You can still draft as-is.
                  </p>
                )}
              </div>
            )}

            {/* FP3-078E — Speech production level (Articulation /
                Phonological Processes only). Sits between Target skill
                and Support / condition level. */}
            {SPEECH_PRODUCTION_AREAS.has(chipSkillArea) && (
              <div className="mb-3" data-testid="ai-speech-level-section">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                  Speech production level
                </Label>
                <ChipGroup
                  testid="ai-chip-speech-level"
                  options={SPEECH_PRODUCTION_LEVELS}
                  value={chipSpeechLevel}
                  onSelect={setChipSpeechLevel}
                />
              </div>
            )}

            {/* Support / condition level */}
            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                Support / condition level
              </Label>
              <ChipGroup
                testid="ai-chip-support-level"
                options={SUPPORT_LEVEL_OPTIONS}
                value={chipSupport}
                onSelect={setChipSupport}
              />
              {chipSupport === "Custom" && (
                <Input
                  data-testid="ai-chip-support-level-custom"
                  value={chipSupportCustom}
                  onChange={(e) => setChipSupportCustom(e.target.value)}
                  placeholder="e.g., during small-group reading"
                  className="mt-2 bg-white border-[#d4dfe8] text-sm"
                />
              )}
            </div>

            {/* Measurement style */}
            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                Measurement style
              </Label>
              <ChipGroup
                testid="ai-chip-measurement-style"
                options={MEASUREMENT_STYLE_OPTIONS}
                value={chipMeasurement}
                onSelect={setChipMeasurement}
              />
              {chipMeasurement === "Custom" && (
                <Input
                  data-testid="ai-chip-measurement-style-custom"
                  value={chipMeasurementCustom}
                  onChange={(e) => setChipMeasurementCustom(e.target.value)}
                  placeholder="e.g., quarterly running record"
                  className="mt-2 bg-white border-[#d4dfe8] text-sm"
                />
              )}
            </div>

            {/* Criteria pattern */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#4A6B8C]/80 mb-1.5 block">
                Criteria / mastery pattern
              </Label>
              <ChipGroup
                testid="ai-chip-criteria"
                options={CRITERIA_PATTERN_OPTIONS}
                value={chipCriteria}
                onSelect={setChipCriteria}
              />
              {chipCriteria === "Custom" && (
                <Input
                  data-testid="ai-chip-criteria-custom"
                  value={chipCriteriaCustom}
                  onChange={(e) => setChipCriteriaCustom(e.target.value)}
                  placeholder="e.g., 75% over 4 weeks"
                  className="mt-2 bg-white border-[#d4dfe8] text-sm"
                />
              )}
            </div>

            {!chipGoalLevel && (
              <div
                data-testid="chip-goal-level-reminder"
                className="mt-3 px-3 py-2 rounded-md bg-[#FCF1DE] border border-[#EFD49A] text-[12px] text-[#8C5F00] leading-snug"
              >
                Choose the goal level so the draft matches the student's current
                instructional need. (Manual save still works without this.)
              </div>
            )}
          </div>

          <Textarea
            data-testid="ai-prompt-input"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder='Optional — leave blank if your chip selections cover it. Example: "Student needs to improve /r/ in conversation with fewer cues"'
            className="bg-white border-[#d4dfe8] focus:ring-[#4A6B8C]/30 focus:border-[#4A6B8C] mb-3 min-h-[80px] text-sm"
          />

          {/* FP3-017/020 — Optional context for AI suggestion is now
              collapsible so the AI Assist card stays calm. The field
              is read-only at draft time and never persisted. */}
          <button
            type="button"
            data-testid="ai-context-toggle"
            onClick={() => setAiContextOpen((v) => !v)}
            className="text-[12px] text-[#4A6B8C] hover:text-[#1E3F33] underline decoration-dotted underline-offset-2 mb-2"
          >
            {aiContextOpen ? "Hide context" : "Add context or baseline notes"}
          </button>
          {aiContextOpen && (
            <div data-testid="ai-context-expanded" className="mb-3">
              <p className="text-[11px] text-[#4A6B8C]/70 mb-1.5">
                Add baseline, teacher concerns, evaluation notes, or recent performance. Used only to draft the suggestion.
              </p>
              <Textarea
                data-testid="ai-context-input"
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                placeholder="e.g., Teacher reports student fatigues after ~5 minutes of writing; latest probe showed 40% accuracy on main idea questions."
                className="bg-white border-[#d4dfe8] focus:ring-[#4A6B8C]/30 focus:border-[#4A6B8C] min-h-[60px] text-sm"
              />
            </div>
          )}

          <Button
            data-testid="ai-generate-btn"
            onClick={handleAIGenerate}
            disabled={aiLoading}
            className="bg-[#4A6B8C] hover:bg-[#3d5a77] text-white rounded-lg text-sm"
          >
            {aiLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Drafting…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Sparkle size={16} weight="duotone" /> Draft Goal
              </span>
            )}
          </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FP3-017 — Suggested Draft preview. Renders only after the AI
          returns. Provider reviews structured sections + missing-info
          reminders, then explicitly clicks "Apply to form". The form
          stays untouched until the provider chooses. */}
      {aiDraft && (
        <Card
          data-testid="ai-draft-panel"
          className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm mb-6"
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
                  Suggested draft · review before applying
                </div>
                <h2 className="text-base font-semibold text-[#1F2937] heading-font">
                  {label_for_key_fallback(aiDraft.category) || aiDraft.target_skill || "Annual Goal Draft"}
                </h2>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="ai-draft-dismiss-btn"
                  variant="outline"
                  size="sm"
                  onClick={dismissDraft}
                  className="border-[#E5E3DB] text-[#6B7280] hover:bg-[#F9F8F6] rounded-lg"
                >
                  Dismiss
                </Button>
                <Button
                  data-testid="ai-draft-apply-btn"
                  size="sm"
                  onClick={applyDraftToForm}
                  className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg"
                >
                  <Check size={14} className="mr-1.5" />
                  Apply to form
                </Button>
              </div>
            </div>

            {/* FP3-019 — Compact view: goal wording + missing-info + actions
                are visible immediately. Structured breakdown is tucked
                under a collapsible section to keep the panel calm. */}
            <div data-testid="suggested-draft-compact">
              {aiDraft.goal_text && (
                <div className="mb-4 p-3 rounded-lg bg-[#FAFBFC] border border-[#F1F2F4]">
                  <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
                    Suggested goal wording
                  </div>
                  <div
                    data-testid="ai-draft-goal-text"
                    className="text-sm text-[#1F2937] leading-relaxed"
                  >
                    {aiDraft.goal_text}
                  </div>
                </div>
              )}

              {Array.isArray(aiDraft.missing_info) && aiDraft.missing_info.length > 0 && (
                <div
                  data-testid="ai-draft-missing-info"
                  className="p-3 rounded-lg bg-[#FCF1DE] border border-[#EFD49A] text-[12px] text-[#8C5F00] leading-snug mb-3"
                >
                  <div className="font-semibold mb-1">A few things would make this stronger:</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {aiDraft.missing_info.map((m) => (
                      <li
                        key={m}
                        data-testid={`ai-draft-missing-${m.replace(/_/g, "-")}`}
                      >
                        {MISSING_INFO_REMINDERS[m] || `Add ${m.replace(/_/g, " ")}.`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Collapsible breakdown — hidden by default */}
            <button
              type="button"
              data-testid="ai-draft-breakdown-toggle"
              onClick={() => setDraftBreakdownOpen((v) => !v)}
              className="text-[12px] text-[#4A6B8C] hover:text-[#1E3F33] underline decoration-dotted underline-offset-2"
            >
              {draftBreakdownOpen ? "Hide goal breakdown" : "View goal breakdown"}
            </button>

            {draftBreakdownOpen && (
              <div
                data-testid="suggested-draft-breakdown"
                className="mt-3 pt-3 border-t border-[#F1F2F4]"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  {[
                    { key: "target_skill", label: "Target skill" },
                    { key: "condition_support", label: "Condition / support" },
                    { key: "measurable_behavior", label: "Measurable behavior" },
                    { key: "measurement_method", label: "Measurement method" },
                    { key: "criteria", label: "Accuracy / criteria" },
                    { key: "timeframe", label: "Timeframe" },
                    { key: "suggested_assessment", label: "Suggested assessment" },
                    { key: "baseline_suggestion", label: "Baseline (suggested)" },
                  ].map(({ key, label }) =>
                    aiDraft[key] ? (
                      <div
                        key={key}
                        data-testid={`ai-draft-${key.replace(/_/g, "-")}`}
                        className="p-2.5 rounded-md bg-[#FAFBFC] border border-[#F1F2F4]"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-0.5">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#1F2937] leading-snug">
                          {aiDraft[key]}
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
                {aiDraft.rationale && (
                  <div
                    data-testid="ai-draft-rationale"
                    className="text-[12px] text-[#6B7280] italic leading-snug"
                  >
                    {aiDraft.rationale}
                  </div>
                )}
                {/* FP3-020 — "What this goal looks like in practice"
                    lives inside the collapsible breakdown so the
                    compact view stays calm. */}
                {aiDraft.practice_example && (
                  <div
                    data-testid="ai-draft-practice-example"
                    className="mt-3 p-3 rounded-md bg-[#F2F5F7] border border-[#D8DEE5]"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[#4A6B8C] mb-1 font-semibold">
                      What this goal looks like in practice
                    </div>
                    <div className="text-[12px] text-[#1F2937] leading-relaxed whitespace-pre-line">
                      {aiDraft.practice_example}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="text-[11px] text-[#9CA3AF] italic mt-3">
              This draft is a suggestion. Review before saving.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Structured Form */}
      <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
              <Target size={18} weight="duotone" className="text-[#1E3F33]" /> Goal Details
            </CardTitle>
            {/* FP3-034C — Direct one-click drafter, only shown in the
                Goal Plan flow. The chip-driven "Guide with AI" stays
                untouched above for the broader Add-Goal workflow. */}
            {!goalId && goalPlanContext && (
              <Button
                type="button"
                data-testid="goal-plan-generate-goal-btn"
                onClick={handleGenerateFromTarget}
                disabled={generatingFromTarget || goalPlanContextLoading}
                size="sm"
                className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] text-white"
              >
                <Sparkle size={12} weight="duotone" className="mr-1" />
                {generatingFromTarget ? "Generating…" : "Generate goal"}
              </Button>
            )}
          </div>
          {!goalId && goalPlanContext && (
            <p className="text-[11px] text-[#6B7280] mt-1">
              Drafts measurable goal fields straight from the selected
              Goal Plan focus. Review and edit before saving.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-5">
          {/* FP3-034C — In-card error banner for the Generate goal
              button (distinct from chip-flow AI errors). */}
          {generatorError && !generatingFromTarget && (
            <div
              data-testid="goal-plan-generator-error"
              data-error-code={generatorError.code}
              className="rounded-md border border-[#E9C7B4] bg-[#FBEFE6] text-[#7A3A2C] p-3 text-[12px] flex items-start justify-between gap-2"
            >
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.08em] font-semibold">
                  {generatorError.code === "budget_exhausted"
                    ? "Universal Key budget exhausted"
                    : generatorError.code === "rate_limited"
                      ? "Claude is rate-limited"
                      : generatorError.code === "ai_timeout"
                        ? "AI took too long"
                        : generatorError.code === "ai_unavailable"
                          ? "AI service didn’t respond in time"
                          : "Couldn’t generate the goal"}
                </div>
                <p className="mt-1">{generatorError.message}</p>
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="goal-plan-generator-error-retry"
                  onClick={handleGenerateFromTarget}
                  className="h-7 text-[11px] border-current bg-white/60 hover:bg-white"
                >
                  Try again
                </Button>
                <button
                  type="button"
                  data-testid="goal-plan-generator-error-dismiss"
                  onClick={() => setGeneratorError(null)}
                  className="text-[11px] opacity-70 hover:opacity-100"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Student</Label>
              <Select value={form.student_id} onValueChange={(v) => updateForm("student_id", v)}>
                <SelectTrigger data-testid="select-student" className="border-[#E5E3DB] rounded-lg">
                  <SelectValue placeholder="Select student" />
                </SelectTrigger>
                <SelectContent>
                  {students.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} (Gr. {s.grade})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">
                Goal Area
                {provider?.role && (
                  <span className="ml-2 normal-case font-normal text-[#9CA3AF]">· {provider.role}</span>
                )}
              </Label>
              <Select value={form.category} onValueChange={(v) => updateForm("category", v)}>
                <SelectTrigger data-testid="select-category" className="border-[#E5E3DB] rounded-lg">
                  <SelectValue placeholder={goalAreas.length ? "Select a goal area" : "Loading…"} />
                </SelectTrigger>
                <SelectContent>
                  {goalAreas.map((a) => (
                    <SelectItem key={a.key} value={a.key} data-testid={`goal-area-option-${a.key}`}>
                      {a.label}
                    </SelectItem>
                  ))}
                  {/* Iter 45k — keep legacy categories selectable when
                      editing a historical goal that pre-dates the role-
                      aware taxonomy. The label is suffixed "(legacy)"
                      so providers know to migrate the value. */}
                  {form.category &&
                    !goalAreas.some((a) => a.key === form.category) &&
                    LEGACY_CATEGORY_LABELS[form.category] && (
                      <SelectItem
                        key={form.category}
                        value={form.category}
                        data-testid={`goal-area-legacy-${form.category}`}
                      >
                        {LEGACY_CATEGORY_LABELS[form.category]}
                      </SelectItem>
                    )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Iter 45k — Specific Skill / Target. Optional; connects to
              the future Goal Plan / Skill Map. Do not overbuild yet. */}
          <div>
            <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">
              Specific Skill / Target
              <span className="ml-2 normal-case font-normal text-[#9CA3AF]">· optional</span>
            </Label>
            <Input
              data-testid="specific-skill-input"
              value={form.specific_skill}
              onChange={(e) => updateForm("specific_skill", e.target.value)}
              placeholder="e.g., /r/ in conversation, narrative retell, handwriting legibility"
              className="border-[#E5E3DB] rounded-lg text-sm"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Measurable Goal Text</Label>
            <Textarea
              data-testid="goal-text-input"
              value={form.goal_text}
              onChange={(e) => updateForm("goal_text", e.target.value)}
              placeholder="Enter the complete, measurable goal statement..."
              className="border-[#E5E3DB] focus:ring-[#8FA998]/50 focus:border-[#8FA998] min-h-[100px] text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Baseline</Label>
              <Input
                data-testid="baseline-input"
                value={form.baseline}
                onChange={(e) => updateForm("baseline", e.target.value)}
                placeholder="Current performance level"
                className="border-[#E5E3DB] rounded-lg text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Criteria</Label>
              <Input
                data-testid="criteria-input"
                value={form.criteria}
                onChange={(e) => updateForm("criteria", e.target.value)}
                placeholder="e.g., 80% accuracy across 3 sessions"
                className="border-[#E5E3DB] rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Target Mastery %</Label>
              <Input
                data-testid="target-mastery-input"
                type="number"
                value={form.target_mastery}
                onChange={(e) => updateForm("target_mastery", parseInt(e.target.value) || 0)}
                className="border-[#E5E3DB] rounded-lg text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Cadence</Label>
              <Select value={form.cadence} onValueChange={(v) => updateForm("cadence", v)}>
                <SelectTrigger data-testid="select-cadence" className="border-[#E5E3DB] rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CADENCES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Target Date</Label>
              <Input
                data-testid="target-date-input"
                type="date"
                value={form.target_date}
                onChange={(e) => updateForm("target_date", e.target.value)}
                className="border-[#E5E3DB] rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">Prompts / Cueing</Label>
            <Input
              data-testid="prompts-input"
              value={form.prompts_cueing}
              onChange={(e) => updateForm("prompts_cueing", e.target.value)}
              placeholder="e.g., minimal verbal cues, visual supports"
              className="border-[#E5E3DB] rounded-lg text-sm"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5 block">IEP Goal Reference</Label>
            <Input
              data-testid="iep-ref-input"
              value={form.iep_goal_reference}
              onChange={(e) => updateForm("iep_goal_reference", e.target.value)}
              placeholder="e.g., IEP Goal 1.1"
              className="border-[#E5E3DB] rounded-lg text-sm"
            />
          </div>

          {/* FP3-020 — Optional collapsible "What this goal looks like in
              practice" field. Saved with the goal (NEW practice_example
              field on Goal model). Manual goals can also use the
              "Generate example from goal" button which calls the
              read-only practice-only endpoint.
              FP3-078E — Visibility is now driven ONLY by
              `practiceFieldOpen`. Previously the section was rendered
              whenever `form.practice_example` was non-empty, which
              meant the "Hide" toggle did nothing once content existed.
              The content is preserved across toggles either way — only
              the section's render is gated. Label adapts: "Add ·
              optional" when there's no content yet, "Show" when the
              section is collapsed but content exists, "Hide" when the
              section is open. */}
          <div data-testid="practice-example-section">
            <button
              type="button"
              data-testid="practice-example-toggle"
              onClick={() => setPracticeFieldOpen((v) => !v)}
              className="text-[12px] text-[#4A6B8C] hover:text-[#1E3F33] underline decoration-dotted underline-offset-2"
            >
              {practiceFieldOpen
                ? "Hide “What this goal looks like in practice”"
                : (form.practice_example && form.practice_example.trim())
                  ? "Show “What this goal looks like in practice”"
                  : "Add “What this goal looks like in practice” · optional"}
            </button>
            {practiceFieldOpen && (
              <div data-testid="practice-example-expanded" className="mt-2">
                <p className="text-[11px] text-[#6B7280] mb-1.5">
                  Add an example prompt, activity, or expected student response so you can explain the goal later.
                </p>
                <Textarea
                  data-testid="practice-example-input"
                  value={form.practice_example}
                  onChange={(e) => updateForm("practice_example", e.target.value)}
                  placeholder="e.g., In a therapy task the provider might prompt..."
                  className="border-[#E5E3DB] rounded-lg text-sm min-h-[100px]"
                />
                <div className="mt-2">
                  <Button
                    data-testid="practice-example-generate-btn"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGeneratePracticeExample}
                    disabled={practiceLoading || !form.goal_text.trim()}
                    className="border-[#d4dfe8] text-[#4A6B8C] hover:bg-[#EDF2F7] rounded-lg text-xs"
                  >
                    {practiceLoading ? (
                      <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 border-2 border-[#4A6B8C]/30 border-t-[#4A6B8C] rounded-full animate-spin" />
                        Generating…
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <Sparkle size={12} weight="duotone" /> Generate example from goal
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Separator className="bg-[#E5E3DB]" />

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              data-testid="save-draft-btn"
              variant="outline"
              onClick={() => handleSave("draft")}
              disabled={saving}
              className="border-[#E5E3DB] text-[#1F2937] hover:bg-[#F9F8F6] rounded-lg text-sm"
            >
              Save as Draft
            </Button>
            <Button
              data-testid="save-active-btn"
              onClick={() => handleSave("active")}
              disabled={saving}
              className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-lg text-sm"
            >
              <Check size={16} className="mr-1.5" /> Save & Activate
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
